import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './UnsubscribeNewsletterPage.scss';

const UnsubscribeNewsletterPage = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(true);
    const [userData, setUserData] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [confirming, setConfirming] = useState(false);

    const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;

    useEffect(() => {
        if (token) {
            verifyToken();
        } else {
            setError('Token de désinscription manquant');
            setLoading(false);
        }
    }, [token]);

    const verifyToken = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${STRAPI_URL}/api/newsletter/verify-unsubscribe/${token}`);
            const data = await response.json();

            if (response.ok && data.success) {
                setUserData(data.user);
                setError('');
            } else {
                setError(data.message || 'Token invalide ou expiré');
            }
        } catch (err) {
            console.error('Erreur lors de la vérification du token:', err);
            setError('Une erreur est survenue lors de la vérification');
        } finally {
            setLoading(false);
        }
    };

    const confirmUnsubscribe = async () => {
        try {
            setConfirming(true);
            const response = await fetch(`${STRAPI_URL}/api/newsletter/confirm-unsubscribe`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token })
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setSuccess(true);
                setError('');
                
            } else {
                setError(data.message || 'Erreur lors de la désinscription');
            }
        } catch (err) {
            console.error('Erreur lors de la désinscription:', err);
            setError('Une erreur est survenue lors de la désinscription');
        } finally {
            setConfirming(false);
        }
    };

    const handleCancel = () => {
        navigate('/');
    };

    if (loading) {
        return (
            <div className="unsubscribe-newsletter-page">
                <Helmet>
                    <title>Désinscription newsletter | Grandir & s'élever ensemble</title>
                    <meta name="description" content="Désinscription de la newsletter" />
                </Helmet>
                <div className="unsubscribe-newsletter-page__logo_container">
                    <picture className="unsubscribe-newsletter-page__logo">
                        <source srcSet={'/assets/logo_transparent.webp'} type="image/webp" />
                        <img src={'/assets/logo_transparent.png'} alt="Logo Grandir & s'élever ensemble" loading="eager" fetchpriority="high" />
                    </picture>
                </div>
                <div className="unsubscribe-newsletter-page__container">
                    <div className="unsubscribe-newsletter-page__loading">
                        <div className="unsubscribe-newsletter-page__spinner"></div>
                        <p>Vérification en cours...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="unsubscribe-newsletter-page">
                <Helmet>
                    <title>Erreur désinscription | Grandir & s'élever ensemble</title>
                </Helmet>
                <div className="unsubscribe-newsletter-page__logo_container">
                    <picture className="unsubscribe-newsletter-page__logo">
                        <source srcSet={'/assets/logo_transparent.webp'} type="image/webp" />
                        <img src={'/assets/logo_transparent.png'} alt="Logo Grandir & s'élever ensemble" loading="eager" fetchpriority="high" />
                    </picture>
                </div>
                <div className="unsubscribe-newsletter-page__container">
                    <div className="unsubscribe-newsletter-page__error">
                        <h1 className="unsubscribe-newsletter-page__title">Oups !</h1>
                        <p className="unsubscribe-newsletter-page__message">{error}</p>
                        <button 
                            onClick={handleCancel}
                            className="unsubscribe-newsletter-page__button unsubscribe-newsletter-page__button--secondary"
                        >
                            Retour à l'accueil
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="unsubscribe-newsletter-page">
                <Helmet>
                    <title>Désinscription confirmée | Grandir & s'élever ensemble</title>
                </Helmet>
                <div className="unsubscribe-newsletter-page__logo_container">
                    <picture className="unsubscribe-newsletter-page__logo">
                        <source srcSet={'/assets/logo_transparent.webp'} type="image/webp" />
                        <img src={'/assets/logo_transparent.png'} alt="Logo Grandir & s'élever ensemble" loading="eager" fetchpriority="high" />
                    </picture>
                </div>
                
                <div className="unsubscribe-newsletter-page__container">
                    <div className="unsubscribe-newsletter-page__success">
                        <h1 className="unsubscribe-newsletter-page__title">Désinscription confirmée</h1>
                        <p className="unsubscribe-newsletter-page__message">
                            Tu as été désinscrit(e) avec succès de ma newsletter.
                        </p>
                        <p className="unsubscribe-newsletter-page__submessage">
                            Je suis tristes de te voir partir, mais je respecte ton choix.<br/>
                            Tu peux toujours te réinscrire à tout moment !
                        </p>
                        <button 
                            onClick={handleCancel}
                            className="unsubscribe-newsletter-page__button unsubscribe-newsletter-page__button--secondary"
                        >
                            Retour à l'accueil maintenant
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="unsubscribe-newsletter-page">
            <Helmet>
                <title>Confirmer la désinscription | Grandir & s'élever ensemble</title>
                <meta name="description" content="Confirmer la désinscription de la newsletter" />
            </Helmet>
            <div className="unsubscribe-newsletter-page__logo_container">
                <picture className="unsubscribe-newsletter-page__logo">
                    <source srcSet={'/assets/logo_transparent.webp'} type="image/webp" />
                    <img src={'/assets/logo_transparent.png'} alt="Logo Grandir & s'élever ensemble" loading="eager" fetchpriority="high" />
                </picture>
            </div>
            
            <div className="unsubscribe-newsletter-page__container">
                <div className="unsubscribe-newsletter-page__content">
                    
                    <h1 className="unsubscribe-newsletter-page__title">
                        Désinscription de la newsletter
                    </h1>
                    
                    <div className="unsubscribe-newsletter-page__user-info">
                        <p className="unsubscribe-newsletter-page__message">
                            Bonjour {userData?.firstname || 'Utilisateur'},
                        </p>
                        <p className="unsubscribe-newsletter-page__email">
                            {userData?.email}
                        </p>
                    </div>

                    <p className="unsubscribe-newsletter-page__question">
                        Es-tu sûr(e) de vouloir te désinscrire de ma newsletter ?
                    </p>

                    <div className="unsubscribe-newsletter-page__info">
                        <p>
                            En te désinscrivant, tu ne recevras plus :
                        </p>
                        <ul className="unsubscribe-newsletter-page__benefits">
                            <li>✨ Mes conseils exclusifs en parentalité consciente</li>
                            <li>🎯 Les nouveaux ateliers, cercles et programmes en lignes</li>
                            <li>💝 Mes ressources gratuites</li>
                            <li>⭐ Les offres exclusives</li>
                        </ul>
                    </div>

                    <div className="unsubscribe-newsletter-page__actions">
                        <button 
                            onClick={confirmUnsubscribe}
                            disabled={confirming}
                            className="unsubscribe-newsletter-page__button unsubscribe-newsletter-page__button--primary"
                        >
                            {confirming ? 'Désinscription...' : 'Oui, me désinscrire'}
                        </button>
                        
                        <button 
                            onClick={handleCancel}
                            className="unsubscribe-newsletter-page__button unsubscribe-newsletter-page__button--secondary"
                        >
                            Non, garder mon abonnement
                        </button>
                    </div>

                    <div className="unsubscribe-newsletter-page__reassurance">
                        <p>
                            Tu peux toujours te réinscrire plus tard depuis mon site web.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UnsubscribeNewsletterPage;